import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Eye, Send, Link2, Sparkles, Copy } from "lucide-react";
import { CreateProposalDialog } from "@/components/proposals/CreateProposalDialog";
import { CreateDigitalProposalDialog } from "@/components/proposals/CreateDigitalProposalDialog";
import { toast } from "sonner";

interface Proposal {
  id: string;
  title: string;
  status: string;
  created_at: string;
  public_share_id: string | null;
}

interface DigitalProposalRow {
  id: string;
  title: string;
  status: string;
  created_at: string;
  share_id: string;
  signed_at: string | null;
}

interface ProposalsTabProps {
  jobId: string;
  proposals: Proposal[];
  jobData?: any;
  onCreateProposal: () => void;
}

export function ProposalsTab({ jobId, proposals, jobData, onCreateProposal }: ProposalsTabProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDigitalDialog, setShowDigitalDialog] = useState(false);
  const [estimateId, setEstimateId] = useState<string | null>(null);
  const [digitalProposals, setDigitalProposals] = useState<DigitalProposalRow[]>([]);

  useEffect(() => {
    const shouldCreate = searchParams.get("createProposal") === "true";
    const estimateParam = searchParams.get("estimateId");
    if (shouldCreate) {
      setEstimateId(estimateParam);
      setShowCreateDialog(true);
      searchParams.delete("createProposal");
      searchParams.delete("estimateId");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const loadDigital = async () => {
    const { data } = await supabase
      .from("digital_proposals")
      .select("id, title, status, created_at, share_id, signed_at")
      .eq("job_id", jobId)
      .order("created_at", { ascending: false });
    setDigitalProposals((data as DigitalProposalRow[]) || []);
  };

  useEffect(() => {
    loadDigital();
  }, [jobId]);

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEstimateId(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "signed":
        return "bg-success text-success-foreground";
      case "sent":
        return "bg-info text-info-foreground";
      case "viewed":
        return "bg-warning text-warning-foreground";
      default:
        return "bg-secondary text-secondary-foreground";
    }
  };

  const copyShareLink = async (shareId: string) => {
    const url = `${window.location.origin}/digital-proposal/${shareId}`;
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  };

  return (
    <div className="container mx-auto px-6 py-6 space-y-8">
      {/* Digital Proposals (web link, sign online) */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Digital Proposals
            </h2>
            <p className="text-sm text-muted-foreground">
              Send a branded web link the customer can review and sign online.
            </p>
          </div>
          <Button onClick={() => setShowDigitalDialog(true)} className="gap-2">
            <Link2 className="w-4 h-4" />
            Send as Digital Link
          </Button>
        </div>

        {digitalProposals.length === 0 ? (
          <Card className="p-8">
            <div className="text-center text-muted-foreground text-sm">
              No digital proposals yet — send a link in seconds.
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {digitalProposals.map((dp) => (
              <Card key={dp.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold mb-1 truncate">{dp.title}</h3>
                      <p className="text-xs text-muted-foreground">
                        Created {new Date(dp.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={getStatusColor(dp.status)}>{dp.status}</Badge>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => navigate(`/digital-proposals/review/${dp.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                      Edit / Review
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => copyShareLink(dp.share_id)}
                    >
                      <Copy className="w-4 h-4" />
                      Copy Share Link
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full gap-2"
                      onClick={() => window.open(`/digital-proposal/${dp.share_id}`, "_blank")}
                    >
                      <Send className="w-4 h-4" />
                      Open Public View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* PDF Proposals */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold">PDF Proposals</h2>
            <p className="text-sm text-muted-foreground">Generate a printable PDF document.</p>
          </div>
          <Button onClick={onCreateProposal} variant="outline" className="gap-2">
            <Plus className="w-4 h-4" />
            New PDF Proposal
          </Button>
        </div>

        {proposals.length === 0 ? (
          <Card className="p-8">
            <div className="text-center">
              <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground text-sm">No PDF proposals created yet</p>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proposals.map((proposal) => (
              <Card key={proposal.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{proposal.title}</h3>
                      <p className="text-xs text-muted-foreground">
                        Created {new Date(proposal.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={getStatusColor(proposal.status)}>{proposal.status}</Badge>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={() => navigate(`/proposals/review/${proposal.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                      View Proposal
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <CreateProposalDialog
        open={showCreateDialog}
        onOpenChange={handleCloseDialog}
        jobId={jobId}
        jobData={jobData || {}}
        estimateId={estimateId}
      />

      <CreateDigitalProposalDialog
        open={showDigitalDialog}
        onOpenChange={(o) => {
          setShowDigitalDialog(o);
          if (!o) loadDigital();
        }}
        jobId={jobId}
        jobData={jobData || {}}
      />
    </div>
  );
}
